/** @format */

import type {PaginationState} from '@tanstack/react-table'
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from 'react'
import useSWR, {type SWRResponse} from 'swr'

import {FullPageLoading} from '@src/components/tailwind/Loading'

import {useLoadingContext} from '@src/contexts'
import {useFetch} from '@src/hooks/useFetch'
import type {FormDetailed} from '@src/types/Form'

import {sanitizeFieldName} from './utils'

type SubmissionsContext = {
  data: SubmissionsApiResponse
  filter: Filter
  overlimitCount: number
  pagination: PaginationState
  removeOverlimitSubmissions: (ids: number[]) => void
  revalidate: () => Promise<SubmissionsApiResponse | undefined>
  search: string
  setFilter: (filter: Filter) => void
  setPagination: Dispatch<SetStateAction<PaginationState>>
  setSearch: (query: string) => void
  spamCount: number
}

type SubmissionsApiResponse = {
  fields: string[]
  // Whether THIS response carries submissions past the retention limit — not
  // whether the form has any. False on every page until the readable
  // submissions run out.
  has_extra: boolean
  // ISO date the monthly limit resets on
  limit_resets_on: string
  // null means no finite limit
  monthly_limit: number | null
  overlimit_counter: number
  // room left in the monthly limit; null means no finite limit
  overlimit_remaining: number | null
  spam_counter: number
  submissions: Submission[]
  total_pages: number
}

export type Submission = {
  _date: string
  _id: number
  _status: SubmissionStatus
} & Record<string, unknown>

export type SubmissionStatus = Record<
  string,
  {
    // old status system
    processed?: boolean
    dispatched?: boolean
    delivered?: boolean
    open?: boolean
    // new status system
    error?: string
    error_code?: string
    message?: string
    status?:
      | 'delivered'
      | 'dispatched'
      | 'open'
      | 'queued'
      | 'pending'
      | 'processed'
      | 'processing'
      | 'retrying'
      | 'reprocessing'
  }
>

export type SubmissionsFolder = 'inbox' | 'spam' | 'overlimit'

type Filter = {
  folder: SubmissionsFolder
}

export const SubmissionsContext = createContext<SubmissionsContext | null>(null)

type SubmissionsContextProviderProps = PropsWithChildren<{
  form: FormDetailed
}>

const DEFAULT_PAGE_SIZE = 10

export function SubmissionsContextProvider(
  props: SubmissionsContextProviderProps,
) {
  const {children, form} = props
  const [filter, setFilter] = useState<Filter>({folder: 'inbox'})
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: form.display_config?.pageSize ?? DEFAULT_PAGE_SIZE,
  })
  const [search, setSearch] = useState('')

  // Get submission counts from the form object (loaded with detailed=True).
  // These are computed from the database when the form is serialized.
  const spamCount = form.spam_count ?? 0

  const {data, mutate: revalidate} = useSubmissionsQuery({
    filter,
    form,
    pagination,
    search,
  })

  if (!data) {
    return <FullPageLoading />
  }

  // Prefer the listing response so the count refreshes on revalidate().
  const overlimitCount = data.overlimit_counter

  // Optimistically drop over-limit rows the server accepted for processing:
  // the worker runs async, so an immediate refetch would still list them.
  // Callers reconcile with a real revalidate() once the worker had time.
  function removeOverlimitSubmissions(ids: number[]) {
    const removed = new Set(ids)
    void revalidate(
      current =>
        current && {
          ...current,
          submissions: current.submissions.filter(s => !removed.has(s._id)),
          overlimit_counter: Math.max(
            0,
            current.overlimit_counter - ids.length,
          ),
          overlimit_remaining:
            current.overlimit_remaining == null
              ? current.overlimit_remaining
              : Math.max(0, current.overlimit_remaining - ids.length),
        },
      {revalidate: false},
    )
  }

  const ctx = {
    data,
    filter,
    overlimitCount,
    pagination,
    removeOverlimitSubmissions,
    revalidate,
    search,
    setFilter,
    setPagination,
    setSearch,
    spamCount,
  } satisfies SubmissionsContext

  return (
    <SubmissionsContext.Provider value={ctx}>
      {children}
    </SubmissionsContext.Provider>
  )
}

export function useSubmissionsContext(): SubmissionsContext {
  const ctx = useContext(SubmissionsContext)
  if (!ctx) {
    throw new Error(
      'useSubmissionsContext must be used within a SubmissionsContextProvider',
    )
  }
  return ctx
}

type UseSubmissionsQueryProps = {
  filter: Filter
  form: FormDetailed
  pagination: PaginationState
  search: string
}

function useSubmissionsQuery(
  props: UseSubmissionsQueryProps,
): SWRResponse<SubmissionsApiResponse> {
  const {filter, form, pagination, search} = props
  const {pageIndex, pageSize} = pagination
  const fetch = useFetch()
  const {ready} = useLoadingContext()
  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('filter.spam', `${filter.folder === 'spam'}`)
    if (filter.folder !== 'spam') {
      // spam wins: the spam folder lists spam regardless of over-limit state
      p.set('filter.overlimit', `${filter.folder === 'overlimit'}`)
    }
    // exclude empty search
    if (search) {
      p.set('query', search)
    }
    p.set('limit', `${pageSize}`)
    p.set('offset', `${pageSize * pageIndex}`)
    return p
  }, [filter.folder, pageIndex, pageSize, search])

  return useSWR(
    `/api-int/forms/${form.hashid}/submissions?${params}`,
    async url => {
      const d: SubmissionsApiResponse = await fetch(url, {method: 'GET'})
      return {
        ...d,
        fields: d.fields.map(sanitizeFieldName),
      }
    },
    {
      dedupingInterval: 480,
      keepPreviousData: true,
      onError: ready,
      onSuccess: ready,
      revalidateOnFocus: false,
    },
  )
}
